const generateHTML = (elements, allCmsContent = {}, classStylesRegistry, combinedClassStylesRegistry, allPages = [], currentContentItemParam = null) => {
  // Add device constant at the top
  const DEVICE = 'desktop'; // Default device

  // Add animation keyframes and general styling CSS at the beginning
  const animationKeyframes = `
    <style>
      /* Animation Keyframes */
      @keyframes fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slide-up {
        from {
          transform: translateY(50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes slide-down {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes bounce {
        0%, 100% { 
          transform: scale(1);
          opacity: 1;
        }
        50% { 
          transform: scale(1.1);
          opacity: 1;
        }
      }

      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-10px); }
        75% { transform: translateX(10px); }
      }

      /* Remove the problematic trigger rules */
      [data-animation-onview] {
        opacity: 1; /* Start visible */
        visibility: visible;
      }

      /* Add class for when element is in view */
      [data-animation-onview].in-view {
        animation-play-state: running !important;
      }

      [data-animation-hover]:hover {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onLoad"] {
        animation-play-state: running !important;
      }

      [data-animation-trigger="onClick"]:active {
        animation-play-state: running !important;
      }
    </style>
  `;

  // Add Intersection Observer script for onView animations
  const animationScript = `
    <script>
      document.addEventListener('DOMContentLoaded', function() {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const element = entry.target;
              const animationProps = element.getAttribute('data-animation-onview');
              if (animationProps) {
                // Reset any existing animation
                element.style.animation = 'none';
                // Force reflow
                element.offsetHeight;
                // Apply all animations
                element.style.animation = animationProps;
                element.style.animationFillMode = 'both';
                element.classList.add('in-view');
                // Optional: unobserve after animation is applied
                observer.unobserve(element);
              }
            }
          });
        }, { 
          threshold: 0.1,
          rootMargin: '50px'
        });

        // Find and observe elements with onView animations
        document.querySelectorAll('[data-animation-onview]').forEach(element => {
          observer.observe(element);
        });

        // Handle click animations
        document.querySelectorAll('[data-animation-click]').forEach(element => {
          element.addEventListener('click', (e) => {
            const animationProps = e.currentTarget.getAttribute('data-animation-click');
            if (animationProps) {
              e.currentTarget.style.animation = 'none';
              e.currentTarget.offsetHeight;
              e.currentTarget.style.animation = animationProps;
            }
          });
        });

        // Handle hover animations
        document.querySelectorAll('[data-animation-hover]').forEach(element => {
          element.addEventListener('mouseenter', (e) => {
            const animationProps = e.currentTarget.getAttribute('data-animation-hover');
            if (animationProps) {
              e.currentTarget.style.animation = animationProps;
            }
          });
          element.addEventListener('mouseleave', (e) => {
            e.currentTarget.style.animation = 'none';
          });
        });
      });
    </script>
  `;

  // Add a function to process animation attributes
  const processAnimationAttributes = (element) => {
    if (!element.interactions?.events) return '';

    const animationEvents = element.interactions.events.filter(event => event.type === 'animation');
    if (!animationEvents.length) return '';

    const animationAttributes = {};
    animationAttributes['data-has-animations'] = 'true';

    // Group animations by trigger type
    const animationsByTrigger = {};
    animationEvents.forEach(event => {
      if (!animationsByTrigger[event.trigger]) {
        animationsByTrigger[event.trigger] = [];
      }
      const { animation, duration, delay, iterations } = event.value;
      animationsByTrigger[event.trigger].push(
        `${animation} ${duration} ${delay} ${iterations === 'infinite' ? 'infinite' : iterations}`
      );
    });

    // Apply animations based on trigger type
    Object.entries(animationsByTrigger).forEach(([trigger, animations]) => {
      const combinedAnimations = animations.join(', ');
      switch (trigger) {
        case 'onView':
          animationAttributes['data-animation-onview'] = combinedAnimations;
          animationAttributes['data-animation-trigger'] = 'onView';
          break;
        case 'onHover':
          animationAttributes['data-animation-hover'] = combinedAnimations;
          animationAttributes['data-animation-trigger'] = 'onHover';
          break;
        case 'onClick':
          animationAttributes['data-animation-click'] = combinedAnimations;
          animationAttributes['data-animation-trigger'] = 'onClick';
          break;
        case 'onLoad':
          animationAttributes['style'] = `animation: ${combinedAnimations}; animation-fill-mode: both;`;
          animationAttributes['data-animation-trigger'] = 'onLoad';
          break;
      }
    });

    return Object.entries(animationAttributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
  };

  // Function to process hover events
  const processHoverEvents = (element) => {
    if (!element.interactions?.events) return '';
    
    const hoverEvents = element.interactions.events.filter(event => event.type === 'hover');
    if (!hoverEvents.length) return '';
    
    const hoverAttributes = {};
    
    // Extract hover in and hover out events
    const hoverInEvent = hoverEvents.find(event => event.direction === 'in');
    const hoverOutEvent = hoverEvents.find(event => event.direction === 'out');
    
    if (hoverInEvent || hoverOutEvent) {
      hoverAttributes['data-has-hover'] = 'true';
      
      // Add transition effect if hover in event exists
      if (hoverInEvent) {
        const transitionDuration = hoverInEvent.value?.transitionDuration || '0.3s';
        hoverAttributes['style'] = `transition: all ${transitionDuration} ease;`;
        
        // Add data attributes based on hover action type
        hoverAttributes['data-hover-in-action'] = hoverInEvent.action;
        
        // Stringify the value object for data attribute - ensure proper escaping
        if (hoverInEvent.value) {
          const jsonValue = JSON.stringify(hoverInEvent.value).replace(/"/g, '&quot;');
          hoverAttributes['data-hover-in-value'] = jsonValue;
        }
        
        // Special handling for toggleClass action with targetId
        if (hoverInEvent.action === 'toggleClass' && hoverInEvent.value?.targetId) {
          hoverAttributes['data-hover-target-id'] = hoverInEvent.value.targetId;
          hoverAttributes['data-hover-class-name'] = hoverInEvent.value.className;
        }
        
        // Special handling for toggle action with targetId
        if (hoverInEvent.action === 'toggle' && hoverInEvent.value?.targetId) {
          hoverAttributes['data-hover-target-id'] = hoverInEvent.value.targetId;
          hoverAttributes['data-hover-display'] = hoverInEvent.value.display || 'block';
        }
      }
      
      // Add hover out data if it exists
      if (hoverOutEvent) {
        hoverAttributes['data-hover-out-action'] = hoverOutEvent.action;
        
        // Stringify the value object for data attribute - ensure proper escaping
        if (hoverOutEvent.value) {
          const jsonValue = JSON.stringify(hoverOutEvent.value).replace(/"/g, '&quot;');
          hoverAttributes['data-hover-out-value'] = jsonValue;
        }
        
        // Special handling for toggleClass action with targetId on hover out
        if (hoverOutEvent.action === 'toggleClass' && hoverOutEvent.value?.targetId) {
          hoverAttributes['data-hover-out-target-id'] = hoverOutEvent.value.targetId;
          hoverAttributes['data-hover-out-class-name'] = hoverOutEvent.value.className;
        }
        
        // Special handling for toggle action with targetId on hover out
        if (hoverOutEvent.action === 'toggle' && hoverOutEvent.value?.targetId) {
          hoverAttributes['data-hover-out-target-id'] = hoverOutEvent.value.targetId;
          hoverAttributes['data-hover-out-display'] = hoverOutEvent.value.display || 'none';
        }
      }
    }
    
    return Object.entries(hoverAttributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
  };
  
  // Function to process tab interaction attributes
  const processTabAttributes = (element) => {
    if (!element.interactions?.events) return '';
    
    const tabEvent = element.interactions.events.find(event => event.type === 'tab' && event.action === 'tabclick');
    if (!tabEvent) return '';
    
    // For content elements that might be tab targets, add attributes
    const { tabsParentId, tabButtonClass, tabsContentParentId, tabContentClass } = tabEvent.value;
    
    // Check if this element is a content parent
    if (tabsContentParentId && element.id.toString() === tabsContentParentId.toString()) {
      return `data-tabs-content-parent="${tabsContentParentId}" data-tab-content-class="${tabContentClass}"`;
    }
    
    // Check if this element is a child of a content parent
    if (element.parentId) {
      const contentParent = elements.find(el => 
        el.id.toString() === element.parentId.toString() && 
        el.id.toString() === tabsContentParentId.toString()
      );
      
      if (contentParent) {
        // Find all siblings
        const siblings = elements.filter(el => el.parentId === contentParent.id);
        // Determine if this is the first child
        const isFirstChild = siblings.length > 0 && siblings[0].id === element.id;
        
        return `data-tab-content="true" data-tab-parent="${tabsContentParentId}" ${isFirstChild ? `class="${element.className || ''} ${tabContentClass}"` : ''}`;
      }
    }
    
    return '';
  };

  // Helper function to find element by ID
  const findElementById = (id) => {
    return elements.find(el => el.id === id || el.id === parseInt(id));
  };

  // Helper function to get full element with children
  const getElementWithChildren = (element) => {
    if (!element) return null;
    
    // Deep clone the element
    const clonedElement = JSON.parse(JSON.stringify(element));
    
    // Replace child IDs with actual elements
    if (clonedElement.children) {
      clonedElement.children = clonedElement.children.map(childId => {
        const child = findElementById(childId);
        return getElementWithChildren(child);
      }).filter(Boolean);
    }
    
    return clonedElement;
  };

  // Add this helper function at the top of the file
  const processStyles = (element, classStylesRegistry, combinedClassStylesRegistry, device = 'desktop') => {
    if (!element || !element.styles) return '';
    
    // If element has className, don't apply any inline styles
    if (element.className) {
      return '';
    }

    // If no className, return all styles inline
    return Object.entries(element.styles)
      .filter(([key, value]) => value !== undefined && !(value instanceof Map))
      .map(([key, value]) => {
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        if (typeof value === 'number' && 
            !['opacity', 'fontWeight', 'lineHeight', 'zIndex'].includes(key)) {
          value = `${value}px`;
        }
        return `${cssKey}: ${value}`;
      })
      .join('; ');
  };

  // Add this helper function to handle tab initialization
  const initializeTabStates = (elements) => {
    const tabGroups = {};
    
    // First group all tabs
    elements.forEach(el => {
      if (el.type === 'button' && el.relatedContentId && el.tabGroupId) {
        if (!tabGroups[el.tabGroupId]) {
          tabGroups[el.tabGroupId] = [];
        }
        tabGroups[el.tabGroupId].push(el);
      }
    });

    // Then update elements with proper states
    const updatedElements = elements.map(el => {
      if (el.tabGroupId) {
        const groupTabs = tabGroups[el.tabGroupId];
        if (groupTabs) {
          console.log('Group tabs:', groupTabs);
          
          // Find the default tab or first tab
          const defaultTab = groupTabs.find(tab => tab.byDefault === true) || groupTabs[0];
          console.log('Default tab:', defaultTab);
          
          if (el.type === 'button') {
            // Set active class on default tab
            const isDefaultTab = el.id === defaultTab.id;
            return {
              ...el,
              className: isDefaultTab ? 'Tab active' : 'Tab'
            };
          } else {
            // Update tab content visibility based on the default tab
            const isActiveContent = el.id === defaultTab.relatedContentId;
            const hasClass = el.className && el.className.length > 0;
            
            return {
              ...el,
              className: el.className || '',
              styles: hasClass ? {} : {
                ...el.styles,
                display: isActiveContent ? 'block' : 'none'
              }
            };
          }
        }
      }
      return el;
    });

    console.log('Updated elements:', updatedElements);
    return updatedElements;
  }; 

  // Traditional tab click handler
  const tabClickJS = `
    function handleTabClick(tabButton) {
      const tabGroupId = tabButton.getAttribute('data-tab-group');
      const contentId = tabButton.getAttribute('data-content-id');
      
      // Update tab buttons in this group
      document.querySelectorAll(\`[data-tab-group="\${tabGroupId}"]\`).forEach(tab => {
        if (tab.tagName === 'BUTTON') {
          tab.className = tab === tabButton ? 'Tab active' : 'Tab';
        }
      });
      
      // Update content visibility
      document.querySelectorAll(\`[data-tab-group="\${tabGroupId}"].tab-content\`).forEach(content => {
        content.style.display = content.id === contentId ? 'block' : 'none';
      });
    }
  `;
  
  // New tab interaction click handler for custom tab interactions
  const newTabInteractionJS = `
    function handleTabInteractionClick(tabButton) {
      // Get tab interaction attributes
      const tabsParentId = tabButton.getAttribute('data-tabs-parent-id');
      const tabButtonClass = tabButton.getAttribute('data-tab-button-class');
      const tabsContentParentId = tabButton.getAttribute('data-tabs-content-parent-id');
      const tabContentClass = tabButton.getAttribute('data-tab-content-class');
      
      if (!tabsParentId || !tabButtonClass || !tabsContentParentId || !tabContentClass) return;
      
      // 1. Find the tab parent element
      const tabsParent = document.querySelector(\`[data-element-id="\${tabsParentId}"]\`);
      if (!tabsParent) return;
      
      // 2. Get all direct children tab buttons
      const tabButtons = Array.from(tabsParent.children).filter(
        child => child.hasAttribute('data-element-id')
      );
      
      // 3. Remove active class from all buttons
      tabButtons.forEach(btn => {
        if (btn.classList.contains(tabButtonClass)) {
          btn.classList.remove(tabButtonClass);
        }
      });
      
      // 4. Add active class to clicked button
      tabButton.classList.add(tabButtonClass);
      
      // 5. Find the index of clicked button among its siblings
      const clickedTabIndex = tabButtons.findIndex(btn => btn === tabButton);
      if (clickedTabIndex === -1) return;
      
      // 6. Find content parent and its children
      const contentParent = document.querySelector(\`[data-element-id="\${tabsContentParentId}"]\`);
      if (!contentParent) return;
      
      const contentElements = Array.from(contentParent.children).filter(
        child => child.hasAttribute('data-element-id')
      );
      
      // 7. Remove active class from all content elements
      contentElements.forEach(content => {
        if (content.classList.contains(tabContentClass)) {
          content.classList.remove(tabContentClass);
        }
      });
      
      // 8. Show the corresponding content at the same index
      if (contentElements[clickedTabIndex]) {
        contentElements[clickedTabIndex].classList.add(tabContentClass);
      }
    }
  `;

  // Initialize tab states
  const initializedElements = initializeTabStates(elements);

  // Helper function to process dynamic fields in a string
  const processDynamicFields = (text, contentItem) => {
    if (!text || !contentItem) return text;
    
    // Replace [field:name] with actual content values
    return text.replace(/\[field:([^\]]+)\]/g, (match, fieldName) => {
      return contentItem.data[fieldName] || match;
    });
  };

  const renderElement = (element, currentContentItem = currentContentItemParam, allCmsContentParam = allCmsContent) => {
    if (!element) return '';

    // If element is an ID, get the actual element
    if (typeof element === 'number' || typeof element === 'string') {
      element = findElementById(element);
      if (!element) return '';
    }

    if (element.type === 'table') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const tableAnimationAttrs = processAnimationAttributes(element);
        const tableAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="table"
          ${tableAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <table ${tableAttributes}>
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </table>
        `;
    }

    if (element.type === 'thead') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const theadAnimationAttrs = processAnimationAttributes(element);
        const theadAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="thead"
          ${theadAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <thead ${theadAttributes}>
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </thead>
        `;
    }
    if (element.type === 'tbody') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const tbodyAnimationAttrs = processAnimationAttributes(element);
        const tbodyAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="tbody"
          ${tbodyAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <tbody ${tbodyAttributes}>
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </tbody>
        `;
    }
    if (element.type === 'tr') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const trAnimationAttrs = processAnimationAttributes(element);
        const trAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="tr"
          ${trAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <tr ${trAttributes}>
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </tr>
        `;
    }
    if (element.type === 'th') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const thAnimationAttrs = processAnimationAttributes(element);
        const thAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="th"
          ${thAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <th ${thAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </th>
        `;
    }
    if (element.type === 'td') {
        const attributesString = Object.entries(element.attributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
        const tdAnimationAttrs = processAnimationAttributes(element);
        const tdAttributes = `
          id="${element.id || ''}"
          class="${element.className || ''}"
          ${attributesString}
          data-element-type="td"
          ${tdAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <td ${tdAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </td>
        `;
    }

    if (element.type === 'checkout-order-item' && element.attributes['data-template'] === 'true') {
     // Convert attributes to string
     const attributesString = Object.entries(element.attributes)
     .map(([key, value]) => `${key}="${value}"`)
     .join(' ');

     const animationAttrs = processAnimationAttributes(element);
  
     // Combine all attributes including animations
     const elementAttributes = `
     id="${element.id || ''}"
     class="${element.className || ''}"
     ${attributesString}
     ${animationAttrs}
     `.trim();
     return `
        <div ${elementAttributes} style="display: none;">
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </div>
      `;
    }

    if(element.type === 'checkout-submit-button') {
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const checkoutSubmitAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      return `
        <button ${checkoutSubmitAttributes}>
          ${element.text || 'Complete Purchase'}
        </button>
      `;
    }

    if (element.type === 'cart') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const cartAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
        
      return `
        <div ${cartAttributes}>
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </div>
      `;
    }

    if (element.type === 'cart-button') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const buttonAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        aria-controls="${element.controls || ''}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
        
      return `
        <button ${buttonAttributes}>
          ${element.text || 'Cart (0)'}
        </button>
      `;
    }

    if (element.type === 'cart-wrapper') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const wrapperAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
        
      return `
        <div ${wrapperAttributes}>
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </div>
      `;
    }

    // Add explicit handling for cart-content element type
    if (element.type === 'cart-content') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const contentAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
        
      return `
        <div ${contentAttributes}>
          ${element.text || ''}
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </div>
      `;
    }

    // Handle map element
    if (element.type === 'map') {
      const mapLocation = element.mapLocation || 'New York, NY';
      const mapZoom = element.mapZoom || '13';
      const mapQuery = encodeURIComponent(mapLocation);
      const mapSrc = `https://maps.google.com/maps?q=${mapQuery}&t=&z=${mapZoom}&ie=UTF8&iwloc=&output=embed`;
      
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const mapAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        data-element-type="map"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      return `
        <div ${mapAttributes}>
          <iframe 
            title="Google Map"
            src="${mapSrc}"
            width="100%" 
            height="100%" 
            style="border: 0" 
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }

    // Handle video element
    if (element.type === 'video') {
      const videoSrc = element.videoSrc || element.src || "https://www.youtube.com/embed/dQw4w9WgXcQ";
      const videoType = element.videoType || 'youtube';
      let embedSrc = '';
      
      // Handle different video platforms
      if (videoType === 'youtube') {
        // Extract YouTube video ID if full URL is provided
        const youtubeId = videoSrc.includes('youtube.com') || videoSrc.includes('youtu.be') 
          ? videoSrc.replace(/.*(?:youtu.be\/|v\/|\/v\/|\/embed\/|\/watch\?v=)([^#\&\?]*).*/, '$1')
          : videoSrc;
        embedSrc = `https://www.youtube.com/embed/${youtubeId}`;
      } else if (videoType === 'vimeo') {
        // Extract Vimeo video ID if full URL is provided
        const vimeoId = videoSrc.includes('vimeo.com')
          ? videoSrc.replace(/.*(?:vimeo.com\/)([^#\&\?]*).*/, '$1')
          : videoSrc;
        embedSrc = `https://player.vimeo.com/video/${vimeoId}`;
      } else {
        // Default to treating as direct embed URL
        embedSrc = videoSrc;
      }
      
      // Add query parameters based on settings
      const autoplay = element.autoplay ? '1' : '0';
      const controls = element.controls !== false ? '1' : '0';
      const loop = element.loop ? '1' : '0';
      const mute = element.mute ? '1' : '0';
      
      // Add parameters based on video type
      if (videoType === 'youtube') {
        embedSrc += `?autoplay=${autoplay}&controls=${controls}&loop=${loop}&mute=${mute}`;
      } else if (videoType === 'vimeo') {
        embedSrc += `?autoplay=${autoplay}&loop=${loop}&muted=${mute}`;
      }
      
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const videoAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        data-element-type="video"
        data-video-type="${videoType}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      return `
        <div ${videoAttributes}>
          <iframe 
            title="Video Player"
            src="${embedSrc}"
            width="100%" 
            height="100%" 
            style="border: 0" 
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      `;
    }

    // Handle embed element - renders custom HTML/scripts directly in the published site
    if (element.type === 'embed') {
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      const embedAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        data-element-type="embed"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      // Use the embedCode property if available, otherwise show a placeholder
      const embedContent = element.embedCode 
        ? element.embedCode 
        : '<!-- No embed code provided -->';
      
      return `
        <div ${embedAttributes}>
          ${embedContent}
        </div>
      `;
    }

    if (element.type === 'add-to-cart') {
      console.log('Rendering add-to-cart button:', element);
      
      // Get product title from CMS content item if available
      let productName = 'N/A';
      let productImage = '';
      let productId = 'N/A';
      let productPrice = '0.00';
      
      if (currentContentItem && currentContentItem.data) {           // Get content type fields to find cart-related fields by label
        const contentTypeFields = currentContentItem.contentType?.fields || [];
        
        // Find field names by looking for cart-related labels
        const cartNameField = contentTypeFields.find(field => 
          field.label.toLowerCase().includes('cart') && 
          field.label.toLowerCase().includes('name')
        );
        
        const cartPriceField = contentTypeFields.find(field => 
          field.label.toLowerCase().includes('cart') && 
          field.label.toLowerCase().includes('price')
        );
        
        const cartImageField = contentTypeFields.find(field => 
          field.label.toLowerCase().includes('cart') && 
          field.label.toLowerCase().includes('image')
        );
        
        // Use the field names to get the values from the content item data
        if (cartNameField && currentContentItem.data[cartNameField.name]) {
          productName = currentContentItem.data[cartNameField.name];
        } else if (currentContentItem.data.title || currentContentItem.data.name) {
          // Fallback to regular title/name fields
          productName = currentContentItem.data.title || currentContentItem.data.name;
        }
        
        // Check for cart-specific image field
        if (cartImageField && currentContentItem.data[cartImageField.name]) {
          productImage = currentContentItem.data[cartImageField.name];
        } else if (currentContentItem.data.image || currentContentItem.data.imageUrl || currentContentItem.data.thumbnail || currentContentItem.data.featured_image) {
          // Fallback to regular image fields
          productImage = currentContentItem.data.image || currentContentItem.data.imageUrl || 
                        currentContentItem.data.thumbnail || currentContentItem.data.featured_image || '';
        }
        
        // Check for cart-specific price field
        if (cartPriceField && currentContentItem.data[cartPriceField.name] !== undefined) {
          productPrice = currentContentItem.data[cartPriceField.name].toString();
        } else if (currentContentItem.data.price) {
          // Fallback to regular price field
          productPrice = currentContentItem.data.price.toString();
        }
        
        // Try to get ID
        if (currentContentItem._id) {
          productId = currentContentItem._id;
        }
      }
      
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      
      // Force product name to be included
      const finalProductName = productName;
      console.log('Final product name:', finalProductName);
      console.log('Product ID:', productId);
      console.log('Product price:', productPrice);
      console.log('Product image:', productImage);

      // Create a complete set of attributes
      const allAttributes = {
        ...attributes, // Include any other attributes from the element
        'data-element': 'add-to-cart',
        'data-action': 'add-to-cart',
        'data-product-id': productId,
        'data-product-name': finalProductName,
        'data-price': productPrice,
        'data-image': productImage || '',
        'aria-label': `Add ${finalProductName} to cart`,
        'type': 'button',
      };
      
      // Convert attributes to string
      const attributesString = Object.entries(allAttributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');

      const animationAttrs = processAnimationAttributes(element);
      const buttonAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${attributesString}
        ${animationAttrs}
      `.trim();
      
      return `
        <button ${buttonAttributes}>
          ${element.text || 'Add to Cart'}
        </button>
      `;
    }

    // Handle all other cart-related elements
    if (element.type && (
        element.type === 'empty-cart-message' || 
        element.type === 'shop-now-button' || 
        element.type === 'cart-total' || 
        element.type === 'cart-total-text' || 
        element.type === 'checkout-button'
      )) {
       // Get the appropriate HTML tag based on element type
      const tag = element.type === 'button' || element.type === 'shop-now-button' || element.type === 'checkout-button' 
        ? 'button' 
        : element.type === 'p' || element.type === 'empty-cart-message' || element.type === 'cart-total-text'
        ? 'p'
        : 'div';
      
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      
      // Create a complete set of attributes with data-element explicitly set
      const allAttributes = {
        'data-element': element.type,
        ...attributes
      };
      
      // Get animation attributes
      const animationAttrs = processAnimationAttributes(element);
      
      // Convert attributes to string
      const attributesString = Object.entries(allAttributes)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');
      
      // Combine all attributes including animations
      const elementAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${attributesString}
        ${animationAttrs}
      `.trim();
      
      return `
        <${tag} ${elementAttributes}>
          ${element.text || ''}
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </${tag}>
      `;
     }  

    
    // NOW handle dynamic content fields for template pages
    if (currentContentItem) {
      // If we have a specific content item passed, use it directly
      if (element.dynamicField) {
        const dynamicContent = currentContentItem.data[element.dynamicField];
        if (dynamicContent !== undefined) {
          if (element.type === 'image') {
            element = {
              ...element,
              src: dynamicContent
            };
          } else {
            element = {
              ...element,
              text: dynamicContent
            };
          }
        }
      } else if (element.dynamicImageField) {
        // Handle dynamic image fields specifically
        const dynamicImageUrl = currentContentItem.data[element.dynamicImageField];
        if (dynamicImageUrl !== undefined) {
          element = {
            ...element,
            src: dynamicImageUrl,
            styles: {
              ...element.styles,
              backgroundImage: `url(${dynamicImageUrl})`
            }
          };
        }
      } else if (element.text) {
        const dynamicText = element.text.replace(/\{\{(.*?)\}\}/g, (match, field) => {
          const fieldName = field.trim();
          return currentContentItem.data[fieldName] || match;
        });
        
        element = {
          ...element,
          text: dynamicText
        };
      }
    } 
    // For backward compatibility, also check if allCmsContentParam is an array
    else if (Array.isArray(allCmsContentParam) && allCmsContentParam.length > 0) {
      const contentItem = allCmsContentParam[0];
      
      if (element.dynamicField) {
        const dynamicContent = contentItem.data[element.dynamicField];
        if (dynamicContent !== undefined) {
          if (element.type === 'image') {
            element = {
              ...element,
              src: dynamicContent
            };
          } else {
            element = {
              ...element,
              text: dynamicContent
            };
          }
        }
      } else if (element.dynamicImageField) {
        // Handle dynamic image fields specifically
        const dynamicImageUrl = contentItem.data[element.dynamicImageField];
        if (dynamicImageUrl !== undefined) {
          element = {
            ...element,
            src: dynamicImageUrl,
            styles: {
              ...element.styles,
              backgroundImage: `url(${dynamicImageUrl})`
            }
          };
        }
      } else if (element.text) {
        const dynamicText = element.text.replace(/\{\{(.*?)\}\}/g, (match, field) => {
          const fieldName = field.trim();
          return contentItem.data[fieldName] || match;
        });
        
        element = {
          ...element,
          text: dynamicText
        };
      }
    }
    // If allCmsContentParam is an object with content type arrays, try to find content for the element's content type
    else if (allCmsContentParam && typeof allCmsContentParam === 'object' && element.contentTypeId) {
      const contentItems = allCmsContentParam[element.contentTypeId];
      if (Array.isArray(contentItems) && contentItems.length > 0) {
        const contentItem = contentItems[0];
        console.log('Using content from map for element:', element.type, element.id, element.contentTypeId);
        
        if (element.dynamicField && contentItem.data) {
          const dynamicContent = contentItem.data[element.dynamicField];
          if (dynamicContent !== undefined) {
            if (element.type === 'image') {
              element = {
                ...element,
                src: dynamicContent
              };
            } else {
              element = {
                ...element,
                text: dynamicContent
              };
            }
          }
        } else if (element.dynamicImageField && contentItem.data) {
          // Handle dynamic image fields specifically
          const dynamicImageUrl = contentItem.data[element.dynamicImageField];
          if (dynamicImageUrl !== undefined) {
            element = {
              ...element,
              src: dynamicImageUrl,
              styles: {
                ...element.styles,
                backgroundImage: `url(${dynamicImageUrl})`
              }
            };
          }
        } else if (element.text && contentItem.data) {
          const dynamicText = element.text.replace(/\{\{(.*?)\}\}/g, (match, field) => {
            const fieldName = field.trim();
            return contentItem.data[fieldName] || match;
          });
          
          element = {
            ...element,
            text: dynamicText
          };
        }
      }
    }

    // Handle CMS content elements
    if (element.type === 'cms-content' && element.contentTypeId) {
      const childElements = element.children?.map(childId => {
        const child = findElementById(childId);
        return getElementWithChildren(child);
      }).filter(Boolean) || [];


      // Get content items for this specific CMS element
      // Handle different formats of cmsContent
      let contentItems = [];
      
      if (Array.isArray(allCmsContentParam)) {
        // If it's an array, use it directly
        contentItems = allCmsContentParam;
      } else if (allCmsContentParam && typeof allCmsContentParam === 'object') {
        // If it's an object (map of content types), get the specific content type
        contentItems = allCmsContentParam[element.contentTypeId] || [];
      }

      // If we still don't have content items, try to find them in the global scope
      if (contentItems.length === 0 && typeof window !== 'undefined' && window.__CMS_CONTENT__) {
        contentItems = window.__CMS_CONTENT__[element.contentTypeId] || [];
        console.log('Using global CMS content:', contentItems);
      }

      // Ensure contentItems is an array
      if (!Array.isArray(contentItems)) {
        console.error('Content items is not an array:', contentItems);
        contentItems = [];
      }

      const sortedItems = [...contentItems].sort((a, b) => {
        if (!a || !b) return 0;
        
        const aValue = element.sortBy === 'title' ? 
          (a.data && a.data[element.sortBy]) : 
          new Date((a.data && a.data[element.sortBy]) || (a[element.sortBy]));
        
        const bValue = element.sortBy === 'title' ? 
          (b.data && b.data[element.sortBy]) : 
          new Date((b.data && b.data[element.sortBy]) || (b[element.sortBy]));
        
        return element.sortOrder === 'desc' ? 
          (bValue > aValue ? 1 : -1) : 
          (aValue > bValue ? 1 : -1);
      });

      const limitedItems = sortedItems.slice(0, element.limit || 10);

      const renderCMSElement = (element, item, parentContentTypeId) => {
        if (!element) return '';

        const elementToRender = JSON.parse(JSON.stringify(element));

        // Handle dynamic content fields
        if (elementToRender.dynamicField) {
          const dynamicContent = item.data[elementToRender.dynamicField];
          if (dynamicContent !== undefined) {
            if (elementToRender.type === 'image') {
              elementToRender.src = dynamicContent;
            } else {
              // Keep the template syntax in text but use dynamic content for display
              elementToRender.displayText = dynamicContent;
            }
          }
        }

        // Handle text template variables
        let elementText = elementToRender.displayText || elementToRender.text || '';
        if (elementText) {
          // If text contains template syntax, replace with actual value
          elementText = elementText.replace(/\{\{(.*?)\}\}/g, (match, field) => {
            const fieldName = field.trim();
            return item.data[fieldName] || match;
          });
        }

        // Handle buttons
        if (elementToRender.type === 'button') {
          
          const buttonAttributes = {
            class: elementToRender.className || '',
            id: elementToRender.builderId || '',
            'data-interaction': 'navigation'
          };


          // Use URL pattern if available, otherwise use default template ID format
          if (allCmsContentParam && allCmsContentParam.__urlPatterns && allCmsContentParam.__urlPatterns[parentContentTypeId]) {
            // Process the URL pattern with the item's data
            let pageUrl = allCmsContentParam.__urlPatterns[parentContentTypeId];
            pageUrl = pageUrl.replace(/\[field:([^\]]+)\]/g, (match, fieldName) => {
              return item.data[fieldName] || match;
            });
            
            // Use the processed URL pattern
            buttonAttributes['data-page-id'] = `${pageUrl}`;
            buttonAttributes['data-url-pattern'] = pageUrl;
          } else {
            // Fallback to the default template ID format
            buttonAttributes['data-page-id'] = `template-${parentContentTypeId}-${item._id}`;
          }

          // Get animation attributes
          const animationAttrs = processAnimationAttributes(elementToRender);

          // Convert button attributes to string
          const attributeString = Object.entries(buttonAttributes)
            .filter(([_, value]) => value)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');

          // Combine all attributes including animations
          const finalAttributes = `${attributeString} ${animationAttrs}`.trim();

          return `
            <button ${finalAttributes}>
              ${elementText || 'View Details'}
            </button>
          `;
        }

        const tag = elementToRender.type === 'h1' ? 'h1' 
                  : elementToRender.type === 'button' ? 'button'
                  : elementToRender.type === 'image' ? 'img'
                  : elementToRender.type === 'list' ? 'ul'
                  : elementToRender.type === 'listItem' ? 'li'
                  : 'div';

        const attributes = {
          class: elementToRender.className || '',
          id: elementToRender.id || '',
          'data-builder-id': elementToRender.builderId || '',
          style: processStyles(elementToRender, classStylesRegistry, combinedClassStylesRegistry, DEVICE),
          ...(elementToRender.type === 'image' ? { 
            src: elementToRender.src || '',
            alt: elementToRender.alt || '' 
          } : {})
        };

        // Add interaction attributes
        if (elementToRender.interactions?.events) {
          const clickEvents = elementToRender.interactions.events.filter(event => event.type === 'click');
          
          if (clickEvents.length > 0) {
            attributes['data-interaction'] = 'click';
            
            clickEvents.forEach(event => {
              if (event.sequence === 'first') {
                attributes['data-first-click'] = 'true';
                attributes['data-first-click-action'] = event.action;
                if (event.value.targetId) {
                  const targetElement = findElementById(event.value.targetId);
                  if (targetElement) {
                    attributes['data-first-click-target'] = targetElement.id;
                    // Store display property for toggle action
                    if (event.action === 'toggle') {
                      attributes['data-first-click-display'] = event.value.display || 'block';
                    }
                    // Store className for toggleClass action
                    else if (event.action === 'toggleClass') {
                      attributes['data-first-click-class'] = event.value.className;
                    }
                  }
                }
              } else if (event.sequence === 'second') {
                attributes['data-second-click'] = 'true';
                attributes['data-second-click-action'] = event.action;
                if (event.value.targetId) {
                  const targetElement = findElementById(event.value.targetId);
                  if (targetElement) {
                    attributes['data-second-click-target'] = targetElement.id;
                    // Store display property for toggle action
                    if (event.action === 'toggle') {
                      attributes['data-second-click-display'] = event.value.display || 'block';
                    }
                    // Store className for toggleClass action
                    else if (event.action === 'toggleClass') {
                      attributes['data-second-click-class'] = event.value.className;
                    }
                  }
                }
              }
            });
          }
          
          // Keep existing navigation interaction if no click events
          else if (elementToRender.interactions.navigation) {
            attributes['data-interaction'] = 'navigation';
            attributes['data-page-id'] = elementToRender.interactions.navigation.pageId;
          }
        }

        // Get animation attributes
        const animationAttrs = processAnimationAttributes(elementToRender);

        // Convert attributes to string and combine with animation attributes
        const attributeString = Object.entries(attributes)
          .filter(([_, value]) => value)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');

        const finalAttributes = `${attributeString} ${animationAttrs}`.trim();

        // Handle children recursively
        const childrenHTML = elementToRender.children?.map(child => {
          // If child is an ID, find the actual element
          const childElement = typeof child === 'number' || typeof child === 'string' 
            ? findElementById(child)
            : child;
          return renderCMSElement(childElement, item, parentContentTypeId);
        }).join('') || '';

        if (elementToRender.type === 'image') {
          return `<${tag} ${finalAttributes} />`;
        }

        return `
          <${tag} ${finalAttributes}>
            ${elementText}
            ${childrenHTML}
          </${tag}>
        `;
      };

      return `
        <div class="cms-content-container ${element.displayType || 'list'}" data-cms-id="${element.contentTypeId}">
          ${limitedItems.map(item => `
            <div class="cms-content-item ${element.displayType || 'list'}" data-item-id="${item._id}">
              ${childElements.map(child => 
                renderCMSElement(child, item, element.contentTypeId)
              ).join('')}
            </div>
          `).join('')}
        </div>
      `;
    }

    // Handle tab buttons
    if (element.type === 'button' && element.relatedContentId && element.tabGroupId) {
      // Get the default tab for this group
      const groupTabs = elements.filter(el => 
        el.type === 'button' && 
        el.tabGroupId === element.tabGroupId
      );
      const defaultTab = groupTabs.find(tab => tab.byDefault === true) || groupTabs[0];
      const isDefaultTab = element.id === defaultTab.id;

      const animationAttrs = processAnimationAttributes(element);
      const tabAttributes = `
        id="${element.builderId}"
        class="${isDefaultTab ? 'Tab active' : 'Tab'}"
        onclick="handleTabClick(this)"
        data-tab-group="${element.tabGroupId}"
        data-content-id="${elements.find(el => el.id === element.relatedContentId)?.builderId}"
        ${animationAttrs}
      `.trim();

      return `
        <button ${tabAttributes}>
          ${element.text || ''}
        </button>
      `;
    }

    // Handle tab content
    if (element.tabGroupId && elements.some(el => 
      el.type === 'button' && 
      el.relatedContentId === element.id && 
      el.tabGroupId === element.tabGroupId
    )) {
      const groupTabs = elements.filter(el => 
        el.type === 'button' && 
        el.tabGroupId === element.tabGroupId
      );
      // Find the default tab instead of active tab
      const defaultTab = groupTabs.find(tab => tab.byDefault === true) || groupTabs[0];
      const isVisible = defaultTab && defaultTab.relatedContentId === element.id;
      const hasClass = element.className && element.className.length > 0;

      const animationAttrs = processAnimationAttributes(element);
      const contentAttributes = `
        id="${element.builderId}"
        class="tab-content ${element.className || ''}"
        data-tab-group="${element.tabGroupId}"
        ${!hasClass ? `style="display: ${isVisible ? 'block' : 'none'}"` : ''}
        ${animationAttrs}
      `.trim();

      return `
        <div ${contentAttributes}>
          ${element.text || ''}
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </div>
      `;
    }

    // Handle tab-button with tab interaction events
    if (element.type === 'tab-button') {
      // Look for tab interaction events
      if (element.interactions?.events) {
        const tabEvent = element.interactions.events.find(event => event.type === 'tab' && event.action === 'tabclick');
        
        if (tabEvent) {
          const { tabsParentId, tabButtonClass, tabsContentParentId, tabContentClass } = tabEvent.value;
          
          // Create attributes for the tab button
          const tabButtonAttributes = {
            id: element.id || '',
            class: element.className || '',
            style: processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE),
            'data-element-id': element.id,
            'data-builder-id': element.builderId,
            'data-tabs-parent-id': tabsParentId,
            'data-tab-button-class': tabButtonClass,
            'data-tabs-content-parent-id': tabsContentParentId,
            'data-tab-content-class': tabContentClass,
            onclick: 'handleTabInteractionClick(this)'
          };
          
          // Convert attributes to string
          const attributeString = Object.entries(tabButtonAttributes)
            .filter(([_, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');
          
          return `
            <button ${attributeString}>
              ${element.text || 'Tab'}
              ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
            </button>
          `;
        }
      }
    }
    
    // Regular element rendering
    const tag = element.type === 'h1' ? 'h1' 
              : element.type === 'h2' ? 'h2'
              : element.type === 'h3' ? 'h3'
              : element.type === 'h4' ? 'h4'
              : element.type === 'h5' ? 'h5'
              : element.type === 'h6' ? 'h6'
              : element.type === 'button' ? 'button'
              : element.type === 'tab-button' ? 'button'
              : element.type === 'image' ? 'img'
              : element.type === 'list' ? 'ul'
              : element.type === 'listItem' ? 'li'
              : 'div';

    // Handle buttons consistently whether they're in CMS or regular content
    if (element.type === 'button' && !element.sliderRole) {
      // Start with the element's default attributes
      const buttonAttributes = {
        class: element.className || '',
        id: element.id || '',
        'data-builder-id': element.builderId || '',
        style: processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE),
      };
      
      // Add any attributes from the element.attributes object
      if (element.attributes) {
        Object.entries(element.attributes).forEach(([key, value]) => {
          buttonAttributes[key] = value;
        });
      }

      // Add interaction attributes for buttons
      if (element.interactions?.events) {
        const clickEvents = element.interactions.events.filter(event => event.type === 'click');
        
        if (clickEvents.length > 0) {
          buttonAttributes['data-interaction'] = 'click';
          
          clickEvents.forEach(event => {
            if (event.sequence === 'first') {
              buttonAttributes['data-first-click'] = 'true';
              buttonAttributes['data-first-click-action'] = event.action;
              if (event.value.targetId) {
                const targetElement = findElementById(event.value.targetId);
                if (targetElement) {
                  buttonAttributes['data-first-click-target'] = targetElement.id;
                  // Store display property for toggle action
                  if (event.action === 'toggle') {
                    buttonAttributes['data-first-click-display'] = event.value.display || 'block';
                  }
                  // Store className for toggleClass action
                  else if (event.action === 'toggleClass') {
                    buttonAttributes['data-first-click-class'] = event.value.className;
                  }
                }
              }
            } else if (event.sequence === 'second') {
              buttonAttributes['data-second-click'] = 'true';
              buttonAttributes['data-second-click-action'] = event.action;
              if (event.value.targetId) {
                const targetElement = findElementById(event.value.targetId);
                if (targetElement) {
                  buttonAttributes['data-second-click-target'] = targetElement.id;
                  // Store display property for toggle action
                  if (event.action === 'toggle') {
                    buttonAttributes['data-second-click-display'] = event.value.display || 'block';
                  }
                  // Store className for toggleClass action
                  else if (event.action === 'toggleClass') {
                    buttonAttributes['data-second-click-class'] = event.value.className;
                  }
                }
              }
            }
          });
        }
        // Only add navigation if there are no click events
        else if (element.interactions.navigation) {
          buttonAttributes['data-interaction'] = 'navigation';
          buttonAttributes['data-page-id'] = element.interactions.navigation.pageId;
        }
      }
      // Handle other navigation cases if no interactions
      else if (element.contentTypeId && currentContentItem) {
        buttonAttributes['data-interaction'] = 'navigation';
        buttonAttributes['data-page-id'] = `template-${element.contentTypeId}-${currentContentItem._id}`;
      }
      else if (element.linkTo) {
        buttonAttributes['data-interaction'] = 'navigation';
        buttonAttributes['data-page-id'] = element.linkTo;
      }

      // Get animation attributes
      const animationAttrs = processAnimationAttributes(element);

      // Convert attributes to string
      const attributeString = Object.entries(buttonAttributes)
        .filter(([_, value]) => value)
        .map(([key, value]) => `${key}="${value}"`)
        .join(' ');

      // Combine all attributes including animations
      const finalAttributes = `${attributeString} ${animationAttrs}`.trim();

      return `
        <button ${finalAttributes}>
          ${element.text || ''}
        </button>
      `;
    }

    // Create base attributes object for all other elements
    const attributes = {
      class: element.className || '',
      id: element.id || '',
      'data-builder-id': element.builderId || '',
      'data-element-id': element.id || '',
      style: processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE),
      ...(element.type === 'image' ? { 
        src: element.src || '',
        alt: element.alt || '' 
      } : {})
    };

    // Add any attributes from the element.attributes object
    if (element.attributes) {
      Object.entries(element.attributes).forEach(([key, value]) => {
        attributes[key] = value;
      });
    }

    // Add interaction attributes
    if (element.interactions?.events) {
      const clickEvents = element.interactions.events.filter(event => event.type === 'click');
      
      if (clickEvents.length > 0) {
        attributes['data-interaction'] = 'click';
        
        clickEvents.forEach(event => {
          if (event.sequence === 'first') {
            attributes['data-first-click'] = 'true';
            attributes['data-first-click-action'] = event.action;
            if (event.value.targetId) {
              const targetElement = findElementById(event.value.targetId);
              if (targetElement) {
                attributes['data-first-click-target'] = targetElement.id;
                // Store display property for toggle action
                if (event.action === 'toggle') {
                  attributes['data-first-click-display'] = event.value.display || 'block';
                }
                // Store className for toggleClass action
                else if (event.action === 'toggleClass') {
                  attributes['data-first-click-class'] = event.value.className;
                }
              }
            }
          } else if (event.sequence === 'second') {
            attributes['data-second-click'] = 'true';
            attributes['data-second-click-action'] = event.action;
            if (event.value.targetId) {
              const targetElement = findElementById(event.value.targetId);
              if (targetElement) {
                attributes['data-second-click-target'] = targetElement.builderId;
                // Store display property for toggle action
                if (event.action === 'toggle') {
                  attributes['data-second-click-display'] = event.value.display || 'block';
                }
                // Store className for toggleClass action
                else if (event.action === 'toggleClass') {
                  attributes['data-second-click-class'] = event.value.className;
                }
              }
            }
          }
        });
      }
      
      // Keep existing navigation interaction if no click events
      else if (element.interactions.navigation) {
        attributes['data-interaction'] = 'navigation';
        attributes['data-page-id'] = element.interactions.navigation.pageId;
      }
    }

    // Add tab attributes if this element has tab-related properties
    const tabAttributes = processTabAttributes(element);
    
    const attributeString = Object.entries(attributes)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ') + ' ' + tabAttributes;

    if (element.type === 'image') {
      // Handle both src and backgroundImage for images
      const imgSrc = element.src || 
        (element.styles?.backgroundImage ? 
          element.styles.backgroundImage.replace(/url\(['"]?(.*?)['"]?\)/g, '$1') : 
          'https://via.placeholder.com/150');
      
      // Process styles using the same function as other elements
      const imageStyles = processStyles(element, classStylesRegistry, combinedClassStylesRegistry);

      // Handle element attributes if present
      let attributesString = '';
      if (element.attributes) {
        attributesString = Object.entries(element.attributes)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ');
      }

      const animationAttrs = processAnimationAttributes(element);
      const imageAttributes = `
        src="${imgSrc}" 
        class="${element.className || ''}"
        alt="${element.text || 'Image'}" 
        style="${imageStyles}"
        ${attributesString}
        ${animationAttrs}
      `.trim();

      return `<img ${imageAttributes}/>`;
    }

    // Add these cases in the renderElement function, alongside other element type handlers

    if (element.type === 'remove-button') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const allAttributes = {
        'data-element': 'remove-button',
        'data-action': 'remove-item',
        'aria-label': 'Remove item from cart',
        'type': 'button',
        ...attributes // Include any other attributes from the element
      };
      
      const animationAttrs = processAnimationAttributes(element);
      const buttonAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(allAttributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      return `
        <button ${buttonAttributes}>
          ${element.text || '×'}
        </button>
      `;
    }

    if (element.type === 'quantity-minus') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const allAttributes = {
        'data-element': 'quantity-minus',
        'data-action': 'decrease-quantity',
        'aria-label': 'Decrease quantity',
        'type': 'button',
        ...attributes // Include any other attributes from the element
      };
      
      const animationAttrs = processAnimationAttributes(element);
      const buttonAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(allAttributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      return `
        <button ${buttonAttributes}>
          ${element.text || '-'}
        </button>
      `;
    }

    if (element.type === 'quantity-plus') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const allAttributes = {
        'data-element': 'quantity-plus',
        'data-action': 'increase-quantity',
        'aria-label': 'Increase quantity',
        'type': 'button',
        ...attributes // Include any other attributes from the element
      };
      
      const animationAttrs = processAnimationAttributes(element);
      const buttonAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(allAttributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      return `
        <button ${buttonAttributes}>
          ${element.text || '+'}
        </button>
      `;
    }

    if (element.type === 'dropdown') {
      // Ensure all attributes from the element are included
      const attributes = element.attributes || {};
      const animationAttrs = processAnimationAttributes(element);
      
      // Create a complete set of attributes
      const dropdownAttributes = `
        id="${element.id || ''}"
        class="${element.className || ''}"
        name="${element.name || ''}"
        style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
        ${animationAttrs}
      `.trim();
      
      // Render the select element with its option children
      return `
        <select ${dropdownAttributes}>
          ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
        </select>
      `;
    }

    if (element.type === 'option') {
      // Ensure all attributes including value are preserved
      const attributes = element.attributes || {};
      const value = element.value || element.text || '';
      
      // Create a complete set of attributes
      const optionAttributes = `
        value="${value}"
        ${element.selected ? 'selected' : ''}
        ${Object.entries(attributes).map(([key, value]) => `${key}="${value}"`).join(' ')}
      `.trim();
      
      // Render the option element
      return `
        <option ${optionAttributes}>${element.text || ''}</option>
      `;
    }

    if (element.type === 'checkbox') {
      const checkboxAttributes = `
        ${attributeString}
        type="checkbox"
      `.trim();
      return `
        <input ${checkboxAttributes} />
      `;
    }

    if (element.type === 'radio') {
      const radioAttributes = `
        ${attributeString}
        type="radio"
      `.trim();
      return `
        <input ${radioAttributes} />
      `;
    }
    

    switch (element.type) {
      case 'rich-text':
        const richTextAnimationAttrs = processAnimationAttributes(element);
        const richTextAttributes = `
          ${attributeString}
          data-element-type="rich-text"
          ${richTextAnimationAttrs}
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
        `.trim();

        return `
          <div ${richTextAttributes}>
            ${element.text || '<p>Rich text content</p>'}
          </div>
        `;

      

      case 'link':
        // Handle link navigation
        const linkHref = element.linkTo && !element.linkTo.startsWith('http') 
          ? `/${element.linkTo}` 
          : element.linkTo || '#';
        
        const linkAnimationAttrs = processAnimationAttributes(element);
        const linkHoverAttrs = processHoverEvents(element);
        const linkAttributes = `
          href="${linkHref}"
          ${attributeString}
          data-element-id="${element.id}"
          ${element.target ? `target="${element.target}"` : ''}
          ${linkAnimationAttrs}
          ${linkHoverAttrs}
        `.trim();
        
        return `
          <a ${linkAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </a>
        `;

      case 'link-block':
        // Handle link-block navigation similar to regular links but with block display
        const linkBlockHref = element.linkTo && !element.linkTo.startsWith('http') 
          ? `/${element.linkTo}` 
          : element.linkTo || '#';
        
        const linkBlockAnimationAttrs = processAnimationAttributes(element);
        const linkBlockHoverAttrs = processHoverEvents(element);
        const linkBlockAttributes = `
          href="${linkBlockHref}"
          ${attributeString}
          data-element-id="${element.id}"
          style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}; display: block; text-decoration: none;"
          ${element.target ? `target="${element.target}"` : ''}
          ${linkBlockAnimationAttrs}
          ${linkBlockHoverAttrs}
        `.trim();
        
        return `
          <a ${linkBlockAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </a>
        `;

      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        // Get animation attributes
        const animationAttrs = processAnimationAttributes(element);
        // Combine all attributes
        const headingAttributes = `${attributeString} ${animationAttrs}`.trim();
        
        return `
          <${tag} ${headingAttributes}>
            ${element.text || ''}
            ${element.children?.map(child => renderElement(child, currentContentItem, allCmsContentParam)).join('') || ''}
          </${tag}>
        `;

      case 'form':
        // Find the builderId of success and error divs
        const successDiv = elements.find(el => el.id === element.successDivId);
        const errorDiv = elements.find(el => el.id === element.errorDivId);
        
        const formAnimationAttrs = processAnimationAttributes(element);
        const formAttributes = `
          ${attributeString}
          name="${element.formName === undefined ? 'Email Form' : element.formName}"
          method="${element.method || 'get'}"
          data-site-id="${window.location.pathname.split('/editor/')[1]}"
          data-success-div-id="${successDiv?.builderId || ''}"
          data-error-div-id="${errorDiv?.builderId || ''}"
          onsubmit="handleFormSubmit(event)"
          ${formAnimationAttrs}
        `.trim();
        
        return `
          <form ${formAttributes}>
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('')}
          </form>
        `;

      case 'input':
        const inputAnimationAttrs = processAnimationAttributes(element);
        const inputAttributes = `
          ${attributeString}
          type="${element.inputType || 'text'}"
          name="${element.name || ''}"
          placeholder="${element.placeholder || ''}"
          ${element.required ? 'required' : ''}
          ${element.autofocus ? 'autofocus' : ''}
          ${inputAnimationAttrs}
        `.trim();
        
        return `<input ${inputAttributes}/>`;

      case 'label':
        const labelAnimationAttrs = processAnimationAttributes(element);
        const labelAttributes = `
          ${attributeString}
          ${labelAnimationAttrs}
        `.trim();
        
        return `
          <label ${labelAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('')}
          </label>
        `;

      case 'submit':
        const submitAnimationAttrs = processAnimationAttributes(element);
        const submitAttributes = `
          ${attributeString}
          type="submit"
          ${submitAnimationAttrs}
        `.trim();
        
        return `
          <button ${submitAttributes}>
            ${element.text || 'Submit'}
          </button>
        `;

      case 'textarea':
        const textareaAnimationAttrs = processAnimationAttributes(element);
        const textareaAttributes = `
          ${attributeString}
          name="${element.name || ''}"
          placeholder="${element.placeholder || ''}"
          rows="4"
          ${element.required ? 'required' : ''}
          ${textareaAnimationAttrs}
        `.trim();
        
        return `
          <textarea ${textareaAttributes}>${element.text || ''}</textarea>
        `;

      case 'slider':
      case 'div':
      case 'button':
        // Check for slider elements based on sliderRole
        if (element.sliderRole) {
          switch (element.sliderRole) {
            case 'container':
              const containerAnimationAttrs = processAnimationAttributes(element);
              // Add device-specific settings as data attributes
              const deviceSettingsAttrs = element.deviceSettings ? `
                data-desktop-settings='${JSON.stringify(element.deviceSettings.desktop || {})}'
                data-tablet-settings='${JSON.stringify(element.deviceSettings.tablet || {})}'
                data-mobile-settings='${JSON.stringify(element.deviceSettings.mobile || {})}'
              ` : '';
              
              const containerAttributes = `
                id="${element.builderId}"
                class="${element.className || ''}"
                data-slider-role="container"
                data-builder-id="${element.builderId}"
                data-slides-per-view="${element.slidesPerView || 1}"
                data-slides-per-scroll="${element.slidesPerScroll || 1}"
                data-loop="${element.attributes?.['data-loop'] || 'false'}"
                data-autoplay="${element.attributes?.['data-autoplay'] || 'false'}"
                data-delay="${element.attributes?.['data-delay'] || '5000'}"
                ${deviceSettingsAttrs}
                style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
                ${containerAnimationAttrs}
              `.trim();
              
              return `
                <div ${containerAttributes}>
                  ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
                </div>
              `;
            case 'mask':
              const maskAnimationAttrs = processAnimationAttributes(element);
              const maskAttributes = `
                id="${element.builderId}"
                class="${element.className || ''}"
                data-slider-role="mask"
                data-builder-id="${element.builderId}"
                data-current-slide="0"
                style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
                ${maskAnimationAttrs}
              `.trim();
              
              return `
                <div ${maskAttributes}>
                  ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
                </div>
              `;
            case 'slide':
              const slideAnimationAttrs = processAnimationAttributes(element);
              const slideAttributes = `
                id="${element.builderId}"
                class="${element.className || ''}"
                data-slider-role="slide"
                data-builder-id="${element.builderId}"
                style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
                ${slideAnimationAttrs}
              `.trim();
              
              return `
                <div ${slideAttributes}>
                  ${element.text || ''}
                  ${element.children?.map(childId => {
                    const child = findElementById(childId);
                    if (!child) return '';
                    return renderElement(child, currentContentItem, allCmsContentParam);
                  }).join('') || ''}
                </div>
              `;
            case 'arrow':
              const arrowAnimationAttrs = processAnimationAttributes(element);
              const arrowAttributes = `
                id="${element.builderId}"
                class="${element.className || ''}"
                data-slider-role="arrow"
                data-builder-id="${element.builderId}"
                data-direction="${element.direction || (element.attributes && element.attributes['data-direction']) || ''}"
                onclick="handleSliderArrowClick(event)"
                style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"
                ${arrowAnimationAttrs}
              `.trim();
              
              // Use <a> tag if element type is link, otherwise use button
              if (element.type === 'link') {
                return `
                  <a href="#" ${arrowAttributes}>
                    ${element.text || ''}
                    ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
                  </a>
                `;
              } else {
                return `
                  <button type="button" ${arrowAttributes}>
                    ${element.text || ''}
                    ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
                  </button>
                `;
              }
          }
        }
        // Fall through to default if not a slider element

      default:
        // Select the appropriate HTML tag based on element type
        let elementType = element.type || 'div';
        
        // Map element type to appropriate HTML tag if needed
        if (element.type === 'table') elementType = 'table';
        else if (element.type === 'thead') elementType = 'thead';
        else if (element.type === 'tbody') elementType = 'tbody';
        else if (element.type === 'tr') elementType = 'tr';
        else if (element.type === 'th') elementType = 'th';
        else if (element.type === 'td') elementType = 'td';
        const hasClass = element.className && element.className.length > 0;
        const defaultAnimationAttrs = processAnimationAttributes(element);
        const hoverAttrs = processHoverEvents(element);
        const defaultAttributes = `
          id="${element.id}"
          data-builder-id="${element.builderId}"
          data-element-id="${element.id}"
          class="${element.className || ''}"
          ${!hasClass ? `style="${processStyles(element, classStylesRegistry, combinedClassStylesRegistry, DEVICE)}"` : ''}
          ${element.onClick ? `onclick="${element.onClick}"` : ''}
          ${attributeString}
          ${defaultAnimationAttrs}
          ${hoverAttrs}
        `.trim();
        
        return `
          <${elementType} ${defaultAttributes}>
            ${element.text || ''}
            ${element.children?.map(childId => renderElement(findElementById(childId), currentContentItem, allCmsContentParam)).join('') || ''}
          </${elementType}>
        `;
    }
  };

  // Update the final HTML to include tab JavaScript
  return `
    <script>${tabClickJS}</script>
    <script>${newTabInteractionJS}</script>
    ${animationKeyframes}
    ${animationScript}
    <script>
      // JavaScript to handle hover events
      document.addEventListener('DOMContentLoaded', function() {
        // Find all elements with hover data attributes
        const hoverElements = document.querySelectorAll('[data-has-hover="true"]');
        
        hoverElements.forEach(function(element) {
          // Get hover in action and values
          const hoverInAction = element.getAttribute('data-hover-in-action');
          const hoverInValueStr = element.getAttribute('data-hover-in-value');
          let hoverInValue = {};
          
          if (hoverInValueStr) {
            try {
              // Replace HTML entities back to their original characters
              const decodedStr = hoverInValueStr.replace(/&quot;/g, '"');
              hoverInValue = JSON.parse(decodedStr);
            } catch (error) {
              console.error('Error parsing hover in value:', error);
            }
          }
          
          // Get hover out action and values
          const hoverOutAction = element.getAttribute('data-hover-out-action');
          const hoverOutValueStr = element.getAttribute('data-hover-out-value');
          let hoverOutValue = {};
          
          if (hoverOutValueStr) {
            try {
              // Replace HTML entities back to their original characters
              const decodedStr = hoverOutValueStr.replace(/&quot;/g, '"');
              hoverOutValue = JSON.parse(decodedStr);
            } catch (error) {
              console.error('Error parsing hover out value:', error);
            }
          }
          
          // Add mouseenter event
          element.addEventListener('mouseenter', function(e) {
            // Store original values to restore them on hover out
            const originalStyle = {};
            
            // Apply hover effect based on action
            switch (hoverInAction) {
              case 'scale':
                originalStyle.transform = element.style.transform || 'scale(1)';
                element.style.transform = 'scale(' + (hoverInValue.scale || 1.1) + ')';
                break;
              case 'opacity':
                originalStyle.opacity = element.style.opacity || '1';
                element.style.opacity = hoverInValue.opacity || 0.8;
                break;
              case 'color':
                originalStyle.color = element.style.color;
                element.style.color = hoverInValue.color || '#3498db';
                break;
              case 'toggleClass':
                if (hoverInValue.className) {
                  // Find target element by ID
                  if (hoverInValue.targetId) {
                    const targetId = parseInt(hoverInValue.targetId);
                    const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                    
                    if (targetElement) {
                      targetElement.classList.add(hoverInValue.className);
                    }
                  } else {
                    // If no targetId, add class to the current element
                    element.classList.add(hoverInValue.className);
                  }
                }
                break;
              case 'toggle':
                // Handle show/hide element on hover
                if (hoverInValue.targetId) {
                  const targetId = parseInt(hoverInValue.targetId);
                  const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                  
                  if (targetElement) {
                    // Save the original display state to restore it later
                    originalStyle.display = targetElement.style.display;
                    
                    // Apply the display property
                    const displayValue = hoverInValue.display || 'block';
                    targetElement.style.setProperty('display', displayValue, 'important');
                  }
                }
                break;
            }
            
            // Store original values for restoration
            element.setAttribute('data-original-style', JSON.stringify(originalStyle));
          });
          
          // Add mouseleave event
          element.addEventListener('mouseleave', function(e) {
            // Get original style from data attribute
            let originalStyle = {};
            try {
              originalStyle = JSON.parse(element.getAttribute('data-original-style') || '{}');
            } catch (error) {
              console.error('Error parsing original style:', error);
            }
            
            // If there's a specific hover out action, apply it
            if (hoverOutAction) {
              switch (hoverOutAction) {
                case 'scale':
                  element.style.transform = 'scale(' + (hoverOutValue.scale || 1) + ')';
                  break;
                case 'opacity':
                  element.style.opacity = hoverOutValue.opacity || 1;
                  break;
                case 'color':
                  element.style.color = hoverOutValue.color || '';
                  break;
                case 'toggleClass':
                  if (hoverOutValue.className) {
                    // Find target element by ID
                    if (hoverOutValue.targetId) {
                      const targetId = parseInt(hoverOutValue.targetId);
                      const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                      
                      if (targetElement) {
                        targetElement.classList.remove(hoverOutValue.className);
                      }
                    } else {
                      // If no targetId, remove class from the current element
                      element.classList.remove(hoverOutValue.className);
                    }
                  }
                  break;
                case 'toggle':
                  // Handle show/hide element on hover out
                  if (hoverOutValue.targetId) {
                    const targetId = parseInt(hoverOutValue.targetId);
                    const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                    
                    if (targetElement) {
                      const displayValue = hoverOutValue.display || 'none';
                      targetElement.style.setProperty('display', displayValue, 'important');
                    }
                  }
                  break;
              }
            } 
            // Otherwise restore to original state based on hover in action
            else if (hoverInAction) {
              switch (hoverInAction) {
                case 'scale':
                  element.style.transform = originalStyle.transform || '';
                  break;
                case 'opacity':
                  element.style.opacity = originalStyle.opacity || '';
                  break;
                case 'color':
                  element.style.color = originalStyle.color || '';
                  break;
                case 'toggleClass':
                  if (hoverInValue.className) {
                    // Find target element by ID
                    if (hoverInValue.targetId) {
                      const targetId = parseInt(hoverInValue.targetId);
                      const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                      
                      if (targetElement) {
                        targetElement.classList.remove(hoverInValue.className);
                      }
                    } else {
                      // If no targetId, remove class from the current element
                      element.classList.remove(hoverInValue.className);
                    }
                  }
                  break;
                case 'toggle':
                  // Restore original display of target element
                  if (hoverInValue.targetId) {
                    const targetId = parseInt(hoverInValue.targetId);
                    const targetElement = document.querySelector('[data-element-id="' + targetId + '"]');
                    
                    if (targetElement && originalStyle.display !== undefined) {
                      targetElement.style.setProperty('display', originalStyle.display, 'important');
                    }
                  }
                  break;
              }
            }
          });
        });
      });
    </script>
    ${initializedElements
      .filter(el => !el.parentId)
      .map(el => renderElement(el))
      .join('')}
  `;
};

export default generateHTML; 